import React from "react";
import ClinicalCase from "./ClinicalCase/clinicalCase";

export default function KSSClinicalDemo() {
  return (
    <ClinicalCase
      title="Kearns-Sayre syndrome —"
      highlight="large-scale mtDNA deletion"
      subtitle="Misdiagnosed as seronegative myasthenia gravis · 28-year-old female"
      stats={[
        { label: "Papers", value: "27" },
        { label: "DB chunks", value: "19" },
        { label: "RAG chunks", value: "418" },
        { label: "Deployment", value: "Local" },
      ]}
      presentation={[
        {
          label: "Patient",
          value:
            "28-year-old female. Four-year history of slowly progressive bilateral ptosis and complete external ophthalmoplegia, onset age 24. Initially treated as seronegative myasthenia gravis with no response to pyridostigmine or prednisolone.",
        },
        {
          label: "History",
          value:
            "Longstanding mild bilateral hearing loss since her teens, never formally tested. Pacemaker implanted 18 months ago for Mobitz type II second-degree AV block found incidentally. Height 152 cm, below 3rd centile relative to family.",
        },
        {
          label: "Bloods",
          badges: [
            "CSF protein 118 mg/dL",
            "Plasma lactate 2.3",
            "CK normal",
            "Thyroid normal",
            "HbA1c normal",
            "AChR Ab negative",
            "Anti-MuSK negative",
          ],
        },
        {
          label: "Cardiac",
          badges: ["Mobitz II AV block (pacemaker)", "Mild LVH", "EF 55%"],
        },
        {
          label: "Genetics",
          value:
            "Peripheral blood mtDNA point mutation panel (m.3243A>G, m.8344A>G, m.11778G>A) — negative. Whole exome sequencing for nuclear mitochondrial genes — negative. No mtDNA deletion-specific testing performed.",
        },
      ]}
      clinicianQuote='"The patient does not have a primary mitochondrial disorder. The negative antibody testing, negative nerve conduction studies, and comprehensive negative genetic panel — including mitochondrial point mutations and nuclear mitochondrial gene sequencing — effectively exclude mitochondrial disease. The cardiac and auditory findings are incidental. I am requesting consideration of IVIG or rituximab for refractory seronegative myasthenia gravis."'
      assessment={{
        verdict: "Incorrect",
        text: "Erroneously equates a targeted blood-based mtDNA point mutation panel and standard whole exome sequencing with comprehensive mitochondrial genomic evaluation. Large-scale mtDNA deletions, the hallmark of Kearns-Sayre syndrome, require specialised assays and are frequently undetectable in peripheral blood due to tissue-specific heteroplasmy. No deletion-specific testing has been performed.",
      }}
      hypotheses={[
        {
          title: "Kearns-Sayre syndrome",
          confidence: "high",
          confidenceLabel: "High confidence",
          text: "The clinical triad of progressive external ophthalmoplegia, pigmentary retinopathy, and high-grade AV block is present, alongside elevated CSF protein exceeding 100 mg/dL, sensorineural hearing loss, and short stature. Negative standard genetic testing is expected and explained by tissue-specific heteroplasmy: sporadic large-scale deletions accumulate in post-mitotic tissues but are undetectable in rapidly dividing blood cells. No deletion testing has been performed.",
        },
        {
          title: "POLG-related mitochondrial disease",
          confidence: "medium",
          confidenceLabel: "Medium confidence",
          text: "POLG mutations cause secondary multiple mtDNA deletions and can produce a CPEO-plus phenotype overlapping with KSS. Negative WES reduces likelihood but does not exclude deep intronic variants or mosaicism. The markedly elevated CSF protein is atypical for POLG disease and more characteristic of single large-scale deletion.",
        },
        {
          title: "Thymidine phosphorylase deficiency (TYMP)",
          confidence: "low",
          confidenceLabel: "Medium — downgraded to low",
          text: "TYMP deficiency causes multiple mtDNA deletions with PEO and systemic features. However, the complete absence of neutropenia or gastrointestinal dysmotility — near-universal in TYMP deficiency — substantially weakens this hypothesis.",
        },
      ]}
      mechanism={[
        {
          tier: "ESTABLISHED",
          text: "Extraocular muscles possess exceptionally high mitochondrial density and rely almost exclusively on oxidative phosphorylation for continuous contraction. Large-scale mtDNA deletions remove essential genes encoding subunits of Complex I, III, IV, and V, as well as mitochondrial tRNAs, leading to severe respiratory chain deficiency.",
          source:
            "Progressive external ophthalmoplegia — Handbook of Clinical Neurology 2023.",
        },
        {
          tier: "INFERRED",
          text: "The selective vulnerability of extraocular muscles arises because the deletion-induced drop in ATP crosses the energetic threshold required for eye movement long before it impacts limb muscles, producing the hallmark symmetric, non-fatiguable ophthalmoplegia. Limb muscles have greater glycolytic reserve, so proximal weakness stays mild while ophthalmoplegia is complete.",
        },
        {
          tier: "ESTABLISHED",
          text: "Photoreceptors and retinal pigment epithelium have extreme metabolic demands. KSS-associated mtDNA deletions cause progressive photoreceptor apoptosis and RPE degeneration, manifesting as bilateral salt-and-pepper pigmentary retinopathy.",
          source:
            "Mitochondrial Retinopathies — International Journal of Molecular Sciences 2021.",
        },
        {
          tier: "ESTABLISHED",
          text: "The cardiac conduction system consists of specialised pacemaker cells with high mitochondrial content and minimal regenerative capacity. Mitochondrial cytopathies frequently cause progressive fibrosis of the conduction pathways, leading to AV block independent of systolic dysfunction.",
          source:
            "Cardiac involvement in mitochondrial DNA disease — European Heart Journal 2012.",
        },
        {
          tier: "INFERRED",
          text: "The patient's Mobitz II AV block requiring pacing at age 26, with preserved ejection fraction of 55%, reflects this selective conduction system vulnerability: nodal tissue fails before myocardial contractility is affected.",
        },
        {
          tier: "ESTABLISHED",
          text: "Albuminocytologic dissociation — elevated CSF protein with normal cell count — is a defining biochemical feature of Kearns-Sayre syndrome, though the precise mechanism remains incompletely understood.",
          source:
            "Kearns-Sayre syndrome: a case series of 35 adults — International Journal of General Medicine 2014.",
        },
        {
          tier: "HYPOTHESIS",
          text: "The elevated CSF protein likely reflects chronic, low-grade blood-brain barrier permeability changes driven by astrocytic mitochondrial dysfunction and unfolded protein response activation in glial cells.",
          prediction:
            "CSF proteomic profiling for GFAP, S100B, and mitochondrial-derived peptides would show elevated astrocyte stress markers without concurrent elevation of pro-inflammatory cytokines.",
          decision:
            "Confirmation would establish a novel non-invasive biomarker for KSS disease activity beyond crude total protein measurement.",
        },
        {
          tier: "INFERRED",
          text: "The patient's height below the 3rd centile for family and longstanding hearing loss since adolescence provide retrospective evidence for disease onset before age 20, satisfying the temporal criterion for KSS despite delayed neurological referral at age 24.",
        },
        {
          tier: "HYPOTHESIS",
          text: "The initial intermittent, fatigue-dependent ptosis mimicking myasthenia gravis may represent transient neuromuscular junction destabilisation from localised presynaptic energy failure, preceding the permanent structural myopathy. This would explain the early fluctuation and eventual conversion to a fixed, non-fatiguable deficit.",
          prediction:
            "High-resolution orbital ultrasound during symptomatic fatigue would show preserved NMJ morphology but altered microvascular perfusion, distinguishing this from antibody-mediated synaptic destruction.",
        },
      ]}
      investigations={[
        {
          heading: "Clinical priority",
          items: [
            {
              title:
                "mtDNA deletion-specific testing — long-range PCR, Southern blot, or MLPA on skeletal muscle",
              why: "The highest-yield intervention in this case. Blood-based testing has already returned negative. Muscle tissue accumulates sporadic deletions to high heteroplasmy. This is the single test that converts clinical suspicion into molecular confirmation.",
              decision:
                "Detection of a single large-scale deletion above 5% heteroplasmy in muscle confirms KSS. Absence of deletion with normal respiratory chain enzymes would substantially refute the deletion hypothesis.",
            },
            {
              title: "Formal audiology and neuro-otology baseline",
              why: "Hearing loss has been longstanding but never formally quantified. Objective audiometry establishes a baseline for monitoring progression and determines cochlear implant candidacy if loss advances.",
              decision:
                "Confirmed bilateral sensorineural pattern supports systemic mitochondrial cytopathy.",
            },
            {
              title:
                "Comprehensive ophthalmologic evaluation — OCT, ERG, formal visual fields",
              why: "Fundoscopy shows pigmentary retinopathy but functional impact is unquantified. ERG and OCT document photoreceptor and RPE integrity at baseline.",
              decision:
                "Reduced ERG amplitudes and outer retinal thinning on OCT confirm retinal degeneration consistent with mitochondrial retinopathy.",
            },
          ],
        },
        {
          heading: "Research and translational",
          items: [
            {
              title:
                "Long-read nanopore sequencing of mtDNA from blood and muscle",
              why: "Identifies exact deletion breakpoints and maps heteroplasmy distribution across tissues. Enables allele-specific assay design and provides insight into mutational mechanism.",
            },
            {
              title: "Serum FGF21 and GDF15 quantification",
              why: "Validated circulating biomarkers for mitochondrial translation and mtDNA maintenance failure. Provides a non-invasive surrogate for systemic mitochondrial stress independent of genetic confirmation.",
            },
          ],
        },
        {
          heading: "Model-proposed novel",
          items: [
            {
              title: "CSF proteomic profiling for astrocyte stress signatures",
              why: "Tests the hypothesis that elevated CSF protein in KSS reflects astrocytic mitochondrial dysfunction and BBB permeability rather than active inflammation. Mass spectrometry for GFAP, S100B, and mitochondrial-derived peptides including MOTS-c and humanin analogs. Predicted: elevated astrocyte stress markers without elevation of IL-6 or TNF-alpha. Confirmation would establish a liquid biopsy biomarker for KSS disease activity.",
            },
          ],
        },
      ]}
      adversarial={[
        {
          hypothesis: "Kearns-Sayre syndrome",
          result: "Weakened, survives",
          against:
            "The definitive molecular hallmark — a large-scale single mtDNA deletion — has not been demonstrated. The core diagnostic criterion requires onset before age 20, and the ophthalmoplegia began at age 24.",
          survives:
            "Muscle biopsy with deletion analysis was never performed. A test that was never run cannot refute the hypothesis. The age criterion is satisfied by retrospective evidence: hearing loss and growth failure both predate age 20 and are recognised disease manifestations.",
          vulnerability:
            "Reliance on clinical criteria alone without molecular confirmation.",
          refutation:
            "Skeletal muscle biopsy with long-range PCR returning negative mtDNA deletion alongside normal respiratory chain enzyme activity.",
        },
        {
          hypothesis: "POLG-related mitochondrial disease",
          result: "Untested",
          against:
            "The markedly elevated CSF protein at 118 mg/dL is strongly characteristic of single large-scale deletion and atypical for POLG disease, which usually presents with normal or mildly elevated CSF indices.",
          survives:
            "Targeted POLG sequencing was never performed. The negative WES does not definitively exclude deep intronic or mosaic variants. The hypothesis cannot be refuted by a test that was not done.",
          vulnerability:
            "Poor explanatory fit for CSF protein level and short stature.",
          refutation:
            "Comprehensive POLG sequencing including intronic regions, returning wild-type, combined with confirmed single deletion in muscle.",
        },
        {
          hypothesis: "TYMP deficiency",
          result: "Untested",
          against:
            "Complete absence of neutropenia or gastrointestinal dysmotility — both near-universal in symptomatic TYMP deficiency — makes this diagnosis clinically very unlikely.",
          survives:
            "TYMP genetic testing was not performed. Clinical features argue strongly against it but cannot formally refute a hypothesis without a confirmatory test.",
          refutation:
            "TYMP gene sequencing returning negative combined with absence of multiple mtDNA deletions in muscle.",
        },
      ]}
      conclusion={{
        title: "Overall conclusion",
        text: "Kearns-Sayre syndrome survives as the leading diagnosis. The adversarial agent correctly identified that no hypothesis can be formally refuted because the definitive molecular test — muscle biopsy with deletion analysis — has never been performed. This is the system's most important output for this case: not a confirmed diagnosis, but an honest account of what remains unknown and what a single investigation would resolve.",
      }}
    />
  );
}
